import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react'

import { CONFIG_ENABLE_ZIP } from '@src/config/env'
import setCookie from '@src/quince-site-common/src/utils/setCookie'

import zipCodeService from '../../../services/zipCodeService'
import AuthUtil from '../../../utils/authUtil'
import getCookie from '../../../utils/getCookie'
import isBrowser from '../../../utils/isBrowser'
import { getZipCodeModalInitialState, validateZipCode } from '../utils/zipcode.utils'

import ZipCodeContext from './zipcode.context'

// @i18n-ignore
export const ZIP_COOKIE_KEY = 'zip-code'
// @i18n-ignore
export const USER_ENTERED_ZIP_COOKIE_KEY = 'user-enter-zip-code'

const ZipCodeContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [zipCode, setZipCode] = useState<string | undefined>(
    getCookie(USER_ENTERED_ZIP_COOKIE_KEY) || getCookie(ZIP_COOKIE_KEY),
  )
  const [isZipcodeModalOpen, openZipcodeModal] = useState(getZipCodeModalInitialState)
  const isLoggedIn = AuthUtil.isLoggedIn()

  const validateAndUpdateZipCode = useCallback(async (zip: string | undefined, isFromAPI = false) => {
    if (!CONFIG_ENABLE_ZIP) return false
    const isValidZip = await validateZipCode(zip)
    if (zip && isValidZip) {
      updateZipCode(zip, isFromAPI)
      return true
    }
    return false
  }, [])

  const fetchZipCode = useCallback(async () => {
    if (!CONFIG_ENABLE_ZIP) return
    try {
      const zip = await zipCodeService.getZipCodeForUser()
      validateAndUpdateZipCode(zip, true).catch((e: unknown) => {
        console.error(e)
      })
    } catch (error) {
      console.error('Error occured during fetching zipcode ', error)
    }
  }, [validateAndUpdateZipCode])

  useEffect(() => {
    if (!getCookie(USER_ENTERED_ZIP_COOKIE_KEY)) {
      fetchZipCode().catch((e: unknown) => {
        console.error(e)
      })
    }
  }, [fetchZipCode, isLoggedIn])

  const updateZipCode = (zip: string, isFromAPI: boolean) => {
    if (!CONFIG_ENABLE_ZIP) return
    if (isBrowser()) {
      setCookie(ZIP_COOKIE_KEY, zip)
      if (!isFromAPI) {
        setCookie(USER_ENTERED_ZIP_COOKIE_KEY, zip)
      }
    }
    setZipCode(zip)
  }

  const handleModalOpen = useCallback((val: boolean) => {
    if (!CONFIG_ENABLE_ZIP) return
    if (val) {
      window.document.body.classList.add('overlay-open')
    } else {
      window.document.body.classList.remove('overlay-open')
    }
    openZipcodeModal(val)
  }, [])

  return (
    <ZipCodeContext.Provider
      value={{
        zipCode,
        updateZipCode: validateAndUpdateZipCode,
        isZipcodeModalOpen,
        openZipcodeModal: handleModalOpen,
      }}
    >
      {children}
    </ZipCodeContext.Provider>
  )
}

export default ZipCodeContextProvider
